import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Clock, CheckCircle, Zap, X, Star, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import FiveStarTestimonials from '../FiveStarTestimonials';

const FiftyUpworkLanding = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Helmet>
        <title>Still Sifting Through 50+ Upwork Proposals Per Role? | HireX8</title>
        <meta name="description" content="Proposal overload is not a hiring strategy. Hire pre-vetted, AI-matched talent in hours with zero platform fees." />
        <meta property="og:title" content="Still Sifting Through 50+ Upwork Proposals Per Role? | HireX8" />
        <meta property="og:description" content="Stop spending hours on proposal triage. Hire pre-vetted, AI-matched talent in hours." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-orange-500">
        {/* Background Image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/50upwork-hero.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center right',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#f45f2c]/95 via-[#f97336]/80 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full py-20 lg:py-24">
          <div className="flex flex-col items-start text-left space-y-5 max-w-3xl">
            {/* Logo */}
            <Link to="/" className="mb-2">
              <span className="text-2xl font-bold text-white drop-shadow-sm">HireX8™</span>
            </Link>

            {/* Badge */}
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-white"></div>
              <span className="font-medium text-white/90">Built for Digital Agencies</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white leading-[0.95] tracking-tight max-w-2xl">
              Why am I still sifting through <span className="text-black">50+ Upwork</span> proposals per role?
            </h1>

            {/* Subheadline */}
            <div className="space-y-1">
              <p className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                This does not scale.
              </p>
              <p className="text-3xl sm:text-4xl text-black leading-tight">
                How are you all handling hiring?
              </p>
            </div>

            {/* Feature bullets */}
            <div className="grid sm:grid-cols-3 gap-3 py-4 w-full max-w-3xl">
              <div className="bg-white/14 backdrop-blur-sm border border-white/20 rounded-xl p-4">
                <Search className="w-6 h-6 text-white mb-3" />
                <h3 className="text-white font-bold mb-1">Pre-vetted talent.</h3>
                <p className="text-white/85 text-sm">Skip the noise. Hire with confidence.</p>
              </div>
              <div className="bg-white/14 backdrop-blur-sm border border-white/20 rounded-xl p-4">
                <Zap className="w-6 h-6 text-white mb-3" />
                <h3 className="text-white font-bold mb-1">AI-matched in hours.</h3>
                <p className="text-white/85 text-sm">We find the right fit, fast.</p>
              </div>
              <div className="bg-white/14 backdrop-blur-sm border border-white/20 rounded-xl p-4">
                <X className="w-6 h-6 text-white mb-3" />
                <h3 className="text-white font-bold mb-1">Zero platform fees.</h3>
                <p className="text-white/85 text-sm">No contracts. No minimums.</p>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col items-start gap-3">
              <a
                href="/meet-with-an-expert"
                className="bg-black hover:bg-gray-900 text-white text-lg font-bold px-8 py-4 rounded-lg inline-flex items-center gap-3 transition-all shadow-xl"
              >
                Talk to an Expert
                <ArrowRight className="w-5 h-5" />
              </a>
              <p className="text-white/70 text-sm">
                Free 20-minute call. No commitment required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-gray-600 font-medium uppercase tracking-wider">How It Works</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              From Proposal Chaos to <span className="text-orange-500">Active Team in Hours</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mt-4">
              Stop wasting cycles screening inboxes. Tell us what you need and get matched with ready-to-deliver talent.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '1', title: 'Tell Us What You Need', desc: 'Describe your project, skills needed, timeline, and budget. Takes 5 minutes.' },
              { step: '2', title: 'Get Matched Instantly', desc: 'Our AI scores every candidate on skill fit, past performance, and project similarity.' },
              { step: '3', title: 'Start Working Today', desc: 'Every match is reviewed by our team before it\'s presented to you. No unpleasant surprises.' },
              { step: '4', title: 'Deliver Excellence', desc: 'Your team hits the ground running. AI-assisted workflows mean better output, faster delivery.' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Talent Types Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              Access <span className="text-orange-500">Every Skill</span> You Need
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mt-4">
              Scale into new services without adding headcount. We cover everything from design to AI.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Design */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="text-4xl mb-4">🎨</div>
              <h3 className="text-2xl font-bold text-orange-500 mb-4">Design</h3>
              <ul className="space-y-2 text-gray-600">
                {['UI/UX Design', 'Brand Identity', 'Marketing Graphics', 'Motion Design'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-orange-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Engineering */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="text-4xl mb-4">💻</div>
              <h3 className="text-2xl font-bold text-orange-500 mb-4">Engineering</h3>
              <ul className="space-y-2 text-gray-600">
                {['Frontend Development', 'Backend Development', 'Mobile Apps', 'AI Integration'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-orange-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Copy */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="text-4xl mb-4">✍️</div>
              <h3 className="text-2xl font-bold text-orange-500 mb-4">Copy</h3>
              <ul className="space-y-2 text-gray-600">
                {['Website Copy', 'Blog & SEO Content', 'Ad Copy', 'Email Campaigns'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-orange-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* AI & Automation */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="text-4xl mb-4">🤖</div>
              <h3 className="text-2xl font-bold text-orange-500 mb-4">AI & Automation</h3>
              <ul className="space-y-2 text-gray-600">
                {['AI Implementation', 'Prompt Engineering', 'Workflow Automation', 'AI-Assisted Production'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-orange-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Agencies Switch Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              Why Agencies Move From <span className="text-orange-500">Upwork</span> to HireX8
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* The bad side */}
            <div className="bg-white rounded-2xl p-8 border border-red-200">
              <div className="flex items-center gap-2 mb-6">
                <Clock className="w-6 h-6 text-red-500" />
                <h3 className="text-xl font-bold text-red-500">Upwork / Freelancer Marketplaces</h3>
              </div>
              <ul className="space-y-4">
                {[
                  '2–4 weeks to find and vet the right person',
                  '15–20% platform fee on every dollar you spend',
                  'You do all the screening — and still gamble on fit',
                  'No delivery guarantee. Miss a deadline? Your problem.',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-600">
                    <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* HireX8 Way */}
            <div className="bg-orange-50 rounded-2xl p-8 border border-orange-200">
              <div className="flex items-center gap-2 mb-6">
                <Zap className="w-6 h-6 text-orange-500" />
                <h3 className="text-xl font-bold text-orange-500">HireX8</h3>
              </div>
              <ul className="space-y-4">
                {[
                  'Matched in hours — team ready same week',
                  'Zero platform fees — your full budget goes to work',
                  'AI-screened and human-verified before they reach you',
                  'Delivery guarantee — 48 hour replacement if needed',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <a
              href="/meet-with-an-expert"
              className="bg-black hover:bg-gray-900 text-white text-lg font-bold px-8 py-4 rounded-lg inline-flex items-center gap-3 transition-all"
            >
              Talk to an Expert — It's Free
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="text-5xl sm:text-6xl font-bold text-orange-500 mb-2">8x</div>
              <p className="text-gray-600">Faster than traditional hiring or freelancer search</p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="text-5xl sm:text-6xl font-bold text-orange-500 mb-2">0%</div>
              <p className="text-gray-600">Platform fees — your full budget goes to execution</p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="text-5xl sm:text-6xl font-bold text-orange-500 mb-2">Hours</div>
              <p className="text-gray-600">To match with pre-vetted talent</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <FiveStarTestimonials flat />

      {/* Final CTA Section */}
      <section className="py-24 px-6 bg-orange-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Your next project doesn't have to wait.
          </h2>

          <p className="text-xl text-white/90 mb-10">
            Talk to one of our agency specialists. We'll map out exactly what talent you need, when you need it — and get you going. No platform fees*. No surprises.
          </p>

          <a
            href="/meet-with-an-expert"
            className="inline-flex items-center gap-3 bg-black hover:bg-gray-900 text-white text-xl font-bold px-12 py-5 rounded-lg shadow-lg transition-all hover:scale-105"
          >
            Book a Free 20-Min Call
            <ArrowRight className="w-6 h-6" />
          </a>

          <p className="mt-6 text-white/70 text-sm">
            No commitment. No sales pressure. Just straight answers.<br />* 3.5% charged for credit card processing or $5 for ACH processing
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-gray-900">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="text-2xl font-bold text-white">
            HireX8™
          </Link>
          <div className="flex items-center gap-6 text-gray-400 text-sm">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <span>© 2026 HireX8. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FiftyUpworkLanding;
